import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Receipt, Check, X, Loader2, ArrowRight, ArrowLeft, FileText, QrCode, CreditCard, Eye, Calendar, UserCircle, Info } from "lucide-react";
import { useAppearance } from "../contexts/AppearanceContext";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtCpfCnpj = (v) => {
  const d = (v || "").replace(/\D/g, "");
  if (d.length <= 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, (_, a, b, c, e) => e ? `${a}.${b}.${c}-${e}` : c ? `${a}.${b}.${c}` : b ? `${a}.${b}` : a);
  return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, (_, a, b, c, e, f) => f ? `${a}.${b}.${c}/${e}-${f}` : e ? `${a}.${b}.${c}/${e}` : c ? `${a}.${b}.${c}` : b ? `${a}.${b}` : a);
};

const BILLING_TYPES = [
  { value: "BOLETO", label: "Boleto", icon: FileText },
  { value: "PIX", label: "Pix", icon: QrCode },
  { value: "CREDIT_CARD", label: "Cartão", icon: CreditCard },
];

export default function BillingWizardModal({ open, onClose, onBillingSent, prefillNegocioId }) {
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3B82F6";
  const tc50 = appearance.colors?.["50"] || "#EFF6FF";
  const currency = appearance.default_currency || "BRL";
  const currencyLocale = currency === "USD" ? "en-US" : currency === "EUR" ? "de-DE" : "pt-BR";
  const fmtValue = (v) => new Intl.NumberFormat(currencyLocale, { style: "currency", currency }).format(v || 0);
  const fmtInput = (v) => {
    if (!v && v !== 0) return "";
    return new Intl.NumberFormat(currencyLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
  };
  const handleValueChange = (rawInput) => {
    // Remove tudo exceto dígitos
    const digits = rawInput.replace(/\D/g, "");
    if (!digits) { setDisplayValue(""); setForm(f => ({ ...f, value: 0 })); return; }
    // Converte para centavos → reais
    const num = parseInt(digits, 10) / 100;
    setForm(f => ({ ...f, value: num }));
    setDisplayValue(fmtInput(num));
  };
  const setValueAndDisplay = (num) => {
    const v = parseFloat(num) || 0;
    setForm(f => ({ ...f, value: v }));
    setDisplayValue(v > 0 ? fmtInput(v) : "");
  };

  const [step, setStep] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [negocios, setNegocios] = useState([]);
  const [form, setForm] = useState({ template_id: "", negocio_id: "", billing_type: "BOLETO", value: "", due_date: "", description: "", installment_count: 1, cpf_cnpj: "" });
  const [displayValue, setDisplayValue] = useState("");
  const [selectedTpl, setSelectedTpl] = useState(null);
  const [enabledTypes, setEnabledTypes] = useState(["BOLETO", "PIX", "CREDIT_CARD"]);
  const [billingData, setBillingData] = useState(null);
  const [sending, setSending] = useState(false);
  const [aiDetailsOpen, setAiDetailsOpen] = useState(false);
  const [validatingCnpj, setValidatingCnpj] = useState(false);
  const [cnpjInfo, setCnpjInfo] = useState(null);

  useEffect(() => {
    if (open) {
      setStep(0);
      setForm({ template_id: "", negocio_id: prefillNegocioId || "", billing_type: "BOLETO", value: "", due_date: "", description: "", installment_count: 1, cpf_cnpj: "" });
      setDisplayValue("");
      setSelectedTpl(null);
      setBillingData(null);
      axios.get(`${API}/asaas/billing-templates`).then(r => setTemplates(r.data || [])).catch(() => {});
      axios.get(`${API}/asaas/negocios-with-contracts`).then(r => setNegocios(r.data || [])).catch(() => {});
      axios.get(`${API}/integrations/asaas`).then(r => {
        const cfg = r.data?.config || {};
        if (cfg.billing_types?.length > 0) setEnabledTypes(cfg.billing_types);
      }).catch(() => {});
      if (prefillNegocioId) {
        axios.get(`${API}/asaas/negocio-billing-data/${prefillNegocioId}`).then(r => setBillingData(r.data)).catch(() => {});
      }
    }
  }, [open, prefillNegocioId]);

  const loadTemplate = (tplId) => {
    const tpl = templates.find(t => t.id === tplId);
    setSelectedTpl(tpl || null);
    if (tpl) {
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + (tpl.default_due_days || 7));
      const tplTypes = tpl.billing_types || ["BOLETO", "PIX", "CREDIT_CARD"];
      setEnabledTypes(tplTypes);
      const vs = tpl.value_source || "fixed";
      const fixedVal = vs === "fixed" ? (tpl.default_value || "") : "";
      setForm(f => ({ ...f, template_id: tplId, billing_type: tplTypes[0] || "BOLETO", value: fixedVal, description: tpl.default_description || "", due_date: dueDate.toISOString().slice(0, 10) }));
      setDisplayValue(fixedVal ? fmtInput(fixedVal) : "");
      // If value_source is dynamic and deal already selected, fetch value
      const negId = form.negocio_id || prefillNegocioId;
      if (vs !== "fixed" && negId) {
        axios.get(`${API}/negocios/${negId}`).then(r => {
          const val = r.data?.[vs] || 0;
          setValueAndDisplay(val);
        }).catch(() => {});
      }
    }
  };

  const onNegocioSelect = async (negId) => {
    setForm(f => ({ ...f, negocio_id: negId }));
    setBillingData(null);
    if (negId) {
      try {
        const r = await axios.get(`${API}/asaas/negocio-billing-data/${negId}`);
        setBillingData(r.data);
        // Auto-fill value from deal if value_source is dynamic
        if (selectedTpl?.value_source && selectedTpl.value_source !== "fixed") {
          try {
            const negRes = await axios.get(`${API}/negocios/${negId}`);
            const neg = negRes.data;
            const val = neg?.[selectedTpl.value_source] || 0;
            setValueAndDisplay(val);
          } catch {}
        }
      } catch {}
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/asaas/billings`, { ...form, value: parseFloat(form.value), cpf_cnpj: form.cpf_cnpj || billingData?.cpf_cnpj || "", pass_customer_fees: selectedTpl?.pass_customer_fees || false });
      if (r.data.ok) {
        toast.success("Cobranca emitida!");
        if (onBillingSent) onBillingSent(r.data);
        onClose();
      } else {
        toast.error(r.data.error || "Erro ao emitir");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Erro";
      toast.error(msg);
    }
    setSending(false);
  };

  const typeLabels = { BOLETO: "Boleto", PIX: "Pix", CREDIT_CARD: "Cartão" };

  if (!open) return null;

  const STEPS = [
    { n: 1, l: "Cobrança" },
    { n: 2, l: "Pagamento" },
    { n: 3, l: "Resumo" },
  ];

  const canAdvanceStep0 = form.template_id && form.negocio_id && billingData?.has_contracts !== false;
  const canAdvanceStep1 = form.billing_type && form.value && parseFloat(form.value) > 0 && form.due_date;

  const handleAdvanceStep0 = async () => {
    const cpf = form.cpf_cnpj || billingData?.cpf_cnpj || "";
    const digits = cpf.replace(/\D/g, "");
    if (digits.length === 14) {
      setValidatingCnpj(true);
      try {
        const res = await axios.get(`${API}/validate-cnpj/${digits}`);
        if (!res.data.valid) {
          toast.error(res.data.error || "CNPJ inválido. Corrija na etapa de conferência do contrato.");
          setValidatingCnpj(false);
          return;
        }
        if (!res.data.ativa) {
          toast.error(`CNPJ encontrado mas situação: ${res.data.situacao}. Corrija na etapa de conferência do contrato.`);
          setValidatingCnpj(false);
          return;
        }
        setCnpjInfo(res.data);
        toast.success(`CNPJ válido: ${res.data.razao_social}`);
      } catch {
        toast.error("Erro ao validar CNPJ. Tente novamente.");
        setValidatingCnpj(false);
        return;
      }
      setValidatingCnpj(false);
    }
    setStep(1);
  };

  return createPortal(
    <div className="fixed inset-0 z-[99998] bg-black/60 flex items-center justify-center">
      <div className="rounded-lg shadow-2xl w-full max-w-lg overflow-hidden" style={{backgroundColor:"#f8fafc"}}>
        {/* Header + Timeline */}
        <div className="px-6 pt-5 pb-3">
          <div className="flex items-center justify-between mb-5">
            <p className="font-heading font-bold text-lg text-slate-900 flex items-center gap-2"><Receipt className="w-5 h-5" style={{ color: tc500 }} />Emissão de Cobrança</p>
            <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex items-center w-full px-4">
            {STEPS.map((s, idx) => (
              <div key={s.n} className="flex items-center flex-1 last:flex-none">
                <div className="flex flex-col items-center" style={{ width: "60px" }}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${step >= idx ? "text-white" : "bg-slate-100 text-slate-400"}`} style={step >= idx ? { backgroundColor: tc500 } : undefined}>
                    {step > idx ? <Check className="w-4 h-4" /> : s.n}
                  </div>
                  <span className={`text-[10px] font-medium mt-1 ${step >= idx ? "text-slate-800" : "text-slate-400"}`}>{s.l}</span>
                </div>
                {idx < STEPS.length - 1 && <div className={`h-px flex-1 -mt-4 ${step > idx ? "" : "bg-slate-200"}`} style={step > idx ? { backgroundColor: tc500 } : undefined} />}
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 pb-6 flex flex-col" style={{ minHeight: "300px" }}>
          {/* Step 1: Cobrança (Modelo + Negócio) */}
          {step === 0 && (
            <div className="space-y-4 flex-1">
              <div>
                <Label className="text-sm font-medium text-slate-700">Modelo de cobrança</Label>
                <Select value={form.template_id || "none"} onValueChange={v => { if (v !== "none") loadTemplate(v); else { setForm(f => ({ ...f, template_id: "" })); setSelectedTpl(null); } }}>
                  <SelectTrigger style={{backgroundColor:"#fff"}} className="mt-1.5 h-9 text-sm"><SelectValue placeholder="Selecionar modelo..." /></SelectTrigger>
                  <SelectContent className="z-[99999]">
                    <SelectItem value="none">— Selecionar</SelectItem>
                    {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <Label className="text-sm font-medium text-slate-700">Negócio</Label>
                  <Tooltip><TooltipTrigger asChild><span className="cursor-default"><Info className="w-3.5 h-3.5 text-slate-400" /></span></TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-xs z-[99999]">Apenas negócios que possuem pelo menos um contrato emitido são exibidos nesta lista.</TooltipContent></Tooltip>
                  <div className="flex-1" />
                  {billingData?.ai_details && Object.keys(billingData.ai_details).length > 0 && (
                    <button onClick={() => setAiDetailsOpen(true)} className="text-slate-400 hover:text-slate-600" title="Ver dados da validação"><Eye className="w-3.5 h-3.5" /></button>
                  )}
                </div>
                <Select value={form.negocio_id || "none"} onValueChange={v => onNegocioSelect(v === "none" ? "" : v)} disabled={!!prefillNegocioId}>
                  <SelectTrigger style={{backgroundColor:"#fff"}} className={`mt-1.5 h-9 text-sm ${prefillNegocioId ? "opacity-70" : ""}`}><SelectValue placeholder="Selecionar negócio..." /></SelectTrigger>
                  <SelectContent className="z-[99999]">
                    <SelectItem value="none">— Selecionar</SelectItem>
                    {negocios.map(n => <SelectItem key={n.id} value={n.id}>{n.titulo}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {/* Info cards when negocio selected */}
              {form.negocio_id && (() => {
                const neg = negocios.find(n => n.id === form.negocio_id);
                if (!neg) return null;
                return (
                  <div className="flex gap-2">
                    <div className="flex-1 flex items-center gap-3 p-3 border border-slate-200 rounded-lg" style={{backgroundColor:"#fff"}}>
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: tc50 }}>
                        <Calendar className="w-4 h-4" style={{ color: tc500 }} />
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400">Etapa</p>
                        <p className="text-sm font-semibold text-slate-800">{neg.status === "ganho" ? "Ganho" : neg.estagio_nome || "—"}</p>
                      </div>
                    </div>
                    <div className="flex-1 flex items-center gap-3 p-3 border border-slate-200 rounded-lg" style={{backgroundColor:"#fff"}}>
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: tc50 }}>
                        <UserCircle className="w-4 h-4" style={{ color: tc500 }} />
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400">Executivo</p>
                        <p className="text-sm font-semibold text-slate-800">{neg.responsavel_nome || neg.user_name || "—"}</p>
                      </div>
                    </div>
                  </div>
                );
              })()}
              {billingData && billingData.has_contracts === false && (
                <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-700">
                  Este negócio não possui contrato emitido ou assinado. É necessário emitir um contrato antes de gerar uma cobrança.
                </div>
              )}
              {billingData && billingData.cpf_cnpj && (
                <div className="p-2 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-700 flex items-center gap-2">
                  <Check className="w-3.5 h-3.5 flex-shrink-0" />
                  CPF/CNPJ: <strong>{fmtCpfCnpj(billingData.cpf_cnpj)}</strong>
                  <span className="text-emerald-500">({billingData.source === "manual" ? "Dados conferidos" : billingData.source === "ia" ? "Validação IA" : billingData.source === "empresa" ? "Empresa" : "Contato"})</span>
                </div>
              )}
              {billingData && !billingData.cpf_cnpj && (
                <div className="space-y-2">
                  <div className="p-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700">
                    CPF/CNPJ não encontrado. Informe abaixo:
                  </div>
                  <Input value={fmtCpfCnpj(form.cpf_cnpj)} onChange={e => setForm(f => ({ ...f, cpf_cnpj: e.target.value.replace(/\D/g, "").slice(0, 14) }))} placeholder="000.000.000-00" style={{backgroundColor:"#fff"}} className="h-9 text-sm" />
                </div>
              )}
              <div className="flex justify-between pt-2 mt-auto">
                <div />
                <Button className="text-white" style={{ backgroundColor: tc500 }} disabled={!canAdvanceStep0 || validatingCnpj} onClick={handleAdvanceStep0}>
                  {validatingCnpj ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  <span className="flex items-center gap-1">Avançar<ArrowRight className="w-4 h-4" /></span>
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Pagamento (Tipo + Valor + Vencimento) */}
          {step === 1 && (
            <div className="space-y-4 flex-1">
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Forma de pagamento</Label>
                <div className="flex gap-2">
                  {BILLING_TYPES.filter(t => enabledTypes.includes(t.value)).map(t => {
                    const Icon = t.icon;
                    const isActive = form.billing_type === t.value;
                    return (
                      <button key={t.value} type="button" onClick={() => setForm(f => ({ ...f, billing_type: t.value }))}
                        className={`flex-1 flex items-center justify-center gap-2 p-2.5 rounded-lg border-2 transition-colors ${isActive ? "" : "border-slate-200 text-slate-400 hover:border-slate-300"}`}
                        style={isActive ? { borderColor: tc500, color: tc500, backgroundColor: "#fff" } : { backgroundColor: "#fff" }}>
                        <Icon className="w-4 h-4" />
                        <span className="text-xs font-medium">{t.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Vencimento</Label>
                  <Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} style={{backgroundColor:"#fff"}} className="mt-1 h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Valor ({currency === "BRL" ? "R$" : currency === "USD" ? "$" : currency === "EUR" ? "€" : currency})</Label>
                  <Input type="text" inputMode="numeric" value={displayValue} onChange={e => handleValueChange(e.target.value)} style={{backgroundColor:"#fff"}} className="mt-1 h-9 text-sm" placeholder="0,00" />
                </div>
              </div>
              {form.billing_type === "CREDIT_CARD" && (
                <div>
                  <Label className="text-sm font-medium text-slate-700">Parcelas</Label>
                  <Select value={String(form.installment_count)} onValueChange={v => setForm(f => ({ ...f, installment_count: parseInt(v) }))}>
                    <SelectTrigger style={{backgroundColor:"#fff"}} className="mt-1 h-9 text-sm w-32"><SelectValue /></SelectTrigger>
                    <SelectContent className="z-[99999]">{Array.from({ length: selectedTpl?.max_installments || 12 }, (_, i) => i + 1).map(n => <SelectItem key={n} value={String(n)}>{n}x</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-sm font-medium text-slate-700">Descrição</Label>
                <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={{backgroundColor:"#fff"}} className="mt-1 h-9 text-sm" placeholder="Descrição da cobrança" />
              </div>
              <div className="flex justify-between pt-2 mt-auto">
                <button onClick={() => setStep(0)} className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100"><ArrowLeft className="w-4 h-4" /></button>
                <Button className="text-white" style={{ backgroundColor: tc500 }} disabled={!canAdvanceStep1} onClick={() => setStep(2)}>
                  <span className="flex items-center gap-1">Avançar<ArrowRight className="w-4 h-4" /></span>
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Resumo */}
          {step === 2 && (
            <div className="space-y-3 flex-1">
              <div className="border border-slate-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Modelo</span>
                  <span className="text-sm font-medium text-slate-800">{selectedTpl?.name || "—"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Negócio</span>
                  <span className="text-sm font-medium text-slate-800">{negocios.find(n => n.id === form.negocio_id)?.titulo || "—"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">CPF/CNPJ</span>
                  <span className="text-sm font-medium text-slate-800">{fmtCpfCnpj(form.cpf_cnpj || billingData?.cpf_cnpj || "")}</span>
                </div>
                <div className="border-t border-slate-100 pt-3 flex items-center justify-between">
                  <span className="text-xs text-slate-500">Forma de pagamento</span>
                  <Badge className="border-0 text-xs" style={{ backgroundColor: tc500 + "18", color: tc500 }}>{typeLabels[form.billing_type] || form.billing_type}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Vencimento</span>
                  <span className="text-sm font-medium text-slate-800">{form.due_date ? new Date(form.due_date + "T12:00:00").toLocaleDateString("pt-BR") : "—"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Valor</span>
                  <span className="text-lg font-bold text-slate-900">{fmtValue(form.value)}</span>
                </div>
                {form.billing_type === "CREDIT_CARD" && form.installment_count > 1 && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">Parcelas</span>
                    <span className="text-sm font-medium text-slate-800">{form.installment_count}x de {fmtValue(parseFloat(form.value) / form.installment_count)}</span>
                  </div>
                )}
                {form.description && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">Descrição</span>
                    <span className="text-sm text-slate-600">{form.description}</span>
                  </div>
                )}
              </div>
              <div className="flex justify-between pt-2 mt-auto">
                <button onClick={() => setStep(1)} className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100"><ArrowLeft className="w-4 h-4" /></button>
                <Button className="text-white" style={{ backgroundColor: tc500 }} disabled={sending} onClick={handleSend}>
                  {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Receipt className="w-4 h-4 mr-2" />}Emitir cobrança
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* AI Details Modal */}
      {aiDetailsOpen && createPortal(
        <div className="fixed inset-0 z-[99999] bg-black/50 flex items-center justify-center" onClick={() => setAiDetailsOpen(false)}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md max-h-[80vh] overflow-y-auto p-6 space-y-3" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Eye className="w-4 h-4" style={{ color: tc500 }} />Dados da Validação IA</p>
              <button onClick={() => setAiDetailsOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
            </div>
            {billingData?.ai_details?.status && (
              <div className={`p-2 rounded-lg text-xs font-medium ${billingData.ai_details.status === "APROVADO" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                {billingData.ai_details.status}
              </div>
            )}
            {billingData?.ai_details?.resumo && <p className="text-xs text-slate-600 leading-relaxed">{billingData.ai_details.resumo}</p>}
            {billingData?.ai_details?.empresa && (
              <div className="border border-slate-200 rounded-lg p-3 space-y-1">
                <p className="text-[10px] font-semibold text-slate-400 uppercase">Empresa</p>
                {Object.entries(billingData.ai_details.empresa).filter(([, v]) => typeof v !== "object").map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between">
                    <span className="text-[11px] text-slate-500">{k.replace(/_/g, " ")}</span>
                    <span className="text-[11px] font-medium text-slate-700">{String(v)}</span>
                  </div>
                ))}
              </div>
            )}
            {billingData?.ai_details?.responsavel && (
              <div className="border border-slate-200 rounded-lg p-3 space-y-1">
                <p className="text-[10px] font-semibold text-slate-400 uppercase">Responsável</p>
                {Object.entries(billingData.ai_details.responsavel).filter(([, v]) => typeof v !== "object").map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between">
                    <span className="text-[11px] text-slate-500">{k.replace(/_/g, " ")}</span>
                    <span className="text-[11px] font-medium text-slate-700">{String(v)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>,
    document.body
  );
}
